import React from 'react';
import styled from 'styled-components';

import { ButtonPrimary } from '@yuhu-inc/yuhui';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import Spacer from 'components/ui/Spacer';
import AntdForm from 'components/ui/Form';
import TextBox from 'components/ui/TextBox';
import RadioGroup from 'components/ui/RadioGroup';

import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';

import Select from 'components/ui/SelectField';

import AdditionalAnswer from './AdditionalAnswer';

import { serviceOrUtilityOptions } from '../DocumentTemplateServiceUtilitiesPage';

const StyledFieldCardContainer = styled.div`
  width: 100%;
  margin-top: 15px;
  background-color: ${colors.WHITE};
  border-radius: 16px;
  padding: 24px 24px 0px;
`;

const FullWidthText = styled(TextBox)`
  width: 100%;
`;

const StyledFieldCardCTAContainer = styled.div`
  border-top: 1.5px solid ${colors.PALE_GREY};
  display: flex;
  justify-content: space-between;
  width: 100%;
  justify-items: flex-end;
`;

const StyledCta = styled(ButtonPrimary)`
  background-color: transparent !important;
  font-size: 16px;
`;

const FIELD_TYPES = [
  {
    value: 'text',
    label: getTranslatedString('landlordPortal.documentTemplate.fieldType.text'),
  },
  {
    value: 'radio',
    label: getTranslatedString('landlordPortal.documentTemplate.fieldType.radioButton'),
  },
];

const AdditionalServiceUtility = ({
  additionalServiceUtility,
  values,
  setFieldValue,
  index,
  removeServicesAndUtilitiesFields,
  errors,
}) => {
  const errorCheck = errors['services_and_utilities'] && errors['services_and_utilities'][index];

  const handleAddAnswersToQuestion = () => {
    let newServiceAndUtilties = [...values.services_and_utilities];
    const questionType = newServiceAndUtilties[index].answer_type;

    if (questionType === 'radio') {
      const answersLength = newServiceAndUtilties[index].answers.length;

      if (answersLength > 0) {
        newServiceAndUtilties[index].answers.push({
          answer_name: '',
          answer_value: '',
          selected: false,
          conditional_questions: [],
        });
      } else {
        newServiceAndUtilties[index].answers.push({
          answer_name: '',
          answer_value: '',
          selected: true,
          conditional_questions: [],
        });
      }
    } else {
      newServiceAndUtilties[index].answers.push({
        answer_name: '',
        answer_value: '',
        conditional_questions: [],
      });
    }

    setFieldValue('services_and_utilities', newServiceAndUtilties);
  };

  const handleRemoveServiceUtility = () => {
    let newServiceAndUtilties = [...values.services_and_utilities];
    newServiceAndUtilties.splice(index, 1);
    if (newServiceAndUtilties.length === 0) {
      removeServicesAndUtilitiesFields();
    }
    setFieldValue('services_and_utilities', newServiceAndUtilties);
  };

  const handleAnswerFieldOnTypeChange = (val) => {
    if (val === 'text') {
      setFieldValue(`services_and_utilities[${index}].answers`, [
        {
          answer_name: '',
          answer_value: '',
          conditional_questions: [],
        },
      ]);
    } else {
      setFieldValue(`services_and_utilities[${index}].answers`, [
        {
          answer_name: '',
          answer_value: '',
          selected: true,
          conditional_questions: [],
        },
      ]);
    }
    setFieldValue(`services_and_utilities[${index}].answer_type`, val);
  };

  return (
    <StyledFieldCardContainer>
      <h2>{`${getTranslatedString('landlordPortal.documentTemplate.question')} ${index + 1}`}</h2>
      <Spacer mb={1}>
        <Row gutter={16}>
          <Col span={10}>
            <AntdForm.Item
              label={getTranslatedString('landlordPortal.documentTemplate.questionName')}
              validateStatus={errorCheck?.question_name && 'error'}
              help={errorCheck?.question_name}
            >
              <FullWidthText
                placeholder={getTranslatedString('landlordPortal.maintenance.placeHolder.enter')}
                size="large"
                value={additionalServiceUtility.question_name}
                onChange={(e) =>
                  setFieldValue(`services_and_utilities[${index}].question_name`, e.target.value)
                }
              />
            </AntdForm.Item>
          </Col>

          <Col span={10}>
            <AntdForm.Item
              label={getTranslatedString('landlordPortal.documentTemplate.answerType')}
              validateStatus={errorCheck?.answer_type && 'error'}
              help={errorCheck?.answer_type}
            >
              <Select
                name="answer_type"
                size="large"
                value={additionalServiceUtility.answer_type}
                onChange={(val) => handleAnswerFieldOnTypeChange(val)}
              >
                {FIELD_TYPES.map((fieldType) => (
                  <Select.Option value={fieldType.value}>{fieldType.label}</Select.Option>
                ))}
              </Select>
            </AntdForm.Item>
          </Col>
        </Row>
      </Spacer>

      <Spacer mb={1}>
        <Row gutter={24}>
          <Col span={10}>
            <AntdForm.Item
              label="Type"
              validateStatus={errorCheck?.service_or_utility && 'error'}
              help={errorCheck?.service_or_utility}
            >
              <RadioGroup
                name="service_or_utility"
                options={serviceOrUtilityOptions}
                value={additionalServiceUtility.service_or_utility}
                onChange={(e) => {
                  setFieldValue(
                    `services_and_utilities[${index}].service_or_utility`,
                    e.target.value
                  );
                }}
              />
            </AntdForm.Item>
          </Col>
        </Row>
      </Spacer>

      {values.services_and_utilities[index].answers.map((additionalAnswer, answerIndex) => {
        return (
          <AdditionalAnswer
            answerType={additionalServiceUtility.answer_type}
            values={values}
            additionalAnswer={additionalAnswer}
            setFieldValue={setFieldValue}
            questionIndex={index}
            index={answerIndex}
            errors={errorCheck}
          />
        );
      })}

      <Spacer mb={1}>
        <Row gutter={24}>
          <StyledFieldCardCTAContainer>
            <StyledCta
              type="button"
              contentKey="landlordPortal.documentTemplate.addAnswer"
              onClick={() => handleAddAnswersToQuestion()}
            />

            <StyledCta
              type="button"
              contentKey="landlordPortal.documentTemplate.delete"
              onClick={() => handleRemoveServiceUtility()}
            />
          </StyledFieldCardCTAContainer>
        </Row>
      </Spacer>
    </StyledFieldCardContainer>
  );
};

export default AdditionalServiceUtility;
